import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { useQueryClient } from '@tanstack/react-query'
import { HugeiconsIcon } from '@hugeicons/react'
import { Alert01Icon, Calendar01Icon } from '@hugeicons/core-free-icons'
import { EmptyState } from '@/components/shared/layout'
import {
  useHeatmapPages,
  useAddHeatmapPage,
  useRemoveHeatmapPage,
  useCaptureScreenshot,
  useHeatmapData,
} from '@/hooks/useAnalytics'
import { useURLState } from '@/hooks/useURLState'
import { useFilter } from '@/lib/contexts/FilterContext'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { getPlanDisplayName } from '@/lib/constants/pricing'
import { getAllProperties } from '@/lib/api/api'
import {
  logHeatmapPageAdded,
  logHeatmapPageRemoved,
  logHeatmapScreenshotCaptured,
  logHeatmapTestStarted,
  reportHeatmap403,
  reportHeatmapDownloadFailure,
  reportHeatmapFailure,
  isHeatmapScreenshotTimeout,
} from '@/lib/observability/heatmaps'
import { usePlanUsage, useInvalidatePlanUsage } from '@/hooks/usePlanUsage'
import { PlanLimitBanner, buildPlanLimitCopy } from '@/components/shared/plan-usage'
import {
  HeatmapDownloadAssetError,
  fetchHeatmapScreenshotBlob,
  heatmapDownloadAssetToastMessage,
  isHeatmapDownloadAssetError,
} from '@/lib/utils/heatmapDownload'
import {
  buildVersionsList,
  getDateRangeForVersion,
  formatDateRange,
  isDateRangeWithinFilter,
  normalizeHeatmapResponse,
  type ScreenshotVersion,
} from '@/lib/utils/heatmapUtils'
import { useAIPageContextSafe } from '@/lib/contexts/AIPageContext'
import { AddPageDialog } from './AddPageDialog'
import { HeatmapPageBody } from './HeatmapPageBody'
import { HeatmapLoading, HeatmapNoPages } from './HeatmapStates'
import { HeatmapTopBar } from './HeatmapTopBar'
import type { HeatmapCaptureHandle } from './HeatmapCapture'
import { deviceClickShares, deviceOfPoint, dominantDevice, type HeatmapDevice, type HeatmapMode } from './heatmapInsights'

interface DashboardContext {
  dashboardData: any
  isLoading: boolean
  propertyId?: string
  property?: any
  isShareMode?: boolean
  token?: string
}

const getHeatmapErrorCode = (error: any) => {
  const body = error?.body ?? error?.response?.data
  return typeof body?.error === 'string' ? body.error : undefined
}

const isForbiddenError = (error: any) =>
  error?.status === 403 || error?.message?.includes('status: 403')

/** The page area is full-bleed: cancel DashboardLayout's panel inset. */
const BLEED = '-mx-4 -mt-5 -mb-8 sm:-mx-9 sm:-mt-7 sm:-mb-9 flex min-h-[calc(100svh-68px)] flex-col'

export function HeatmapContainer() {
  const context = useOutletContext<DashboardContext>()
  const params = useParams<{ workspaceId?: string; propertySlugId?: string }>()
  const propertyId = context?.propertyId
  const isShareMode = context?.isShareMode ?? false
  const { token, user } = useAuth()
  const { currentPlan, planName, openPricingModal } = useSubscription()
  const heatmapToken = isShareMode ? context?.token : undefined
  const { period } = useFilter()
  const { updateState } = useURLState()
  const dateFilter = period

  const queryClient = useQueryClient()
  const { data: pagesData, isLoading: pagesLoading } = useHeatmapPages({
    propertyId,
    token: heatmapToken,
  })
  const addPageMutation = useAddHeatmapPage({ propertyId })
  const removePageMutation = useRemoveHeatmapPage({ propertyId })
  const invalidatePlanUsage = useInvalidatePlanUsage()
  const captureScreenshotMutation = useCaptureScreenshot({ propertyId })
  const [awaitingScreenshotFor, setAwaitingScreenshotFor] = useState<string | null>(null)
  const screenshotPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [selectedPageIndex, setSelectedPageIndex] = useState(0)
  const [selectedDeviceType, setSelectedDeviceType] = useState<HeatmapDevice>('desktop')
  const [mode, setMode] = useState<HeatmapMode>('clicks')
  const [selectedVersionIndex, setSelectedVersionIndex] = useState<Map<string, number>>(
    new Map(),
  )
  const [pageVersions, setPageVersions] = useState<Map<string, ScreenshotVersion[]>>(new Map())
  const [showAddPageDialog, setShowAddPageDialog] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [hasAccess, setHasAccess] = useState(true)
  const [isCheckingAccess, setIsCheckingAccess] = useState(true)
  const [capturingScreenshots, setCapturingScreenshots] = useState<Set<string>>(new Set())
  const [isDownloadingHeatmap, setIsDownloadingHeatmap] = useState(false)
  const [isDownloadingScrollDetails, setIsDownloadingScrollDetails] = useState(false)
  const captureRef = useRef<HeatmapCaptureHandle>(null)

  const basePath = isShareMode
    ? null
    : params.workspaceId
      ? `/workspace/${params.workspaceId}`
      : params.propertySlugId
        ? `/p/${params.propertySlugId}`
        : null

  const pages = useMemo(
    () => pagesData?.data?.pages || pagesData?.pages || [],
    [pagesData],
  )

  // Plan-limit awareness (SUPENG-97), wired to the shared usage endpoint.
  // `heatmapsUsage` already encodes trial bypass and the fallback path (see
  // usePlanUsage) — `canAdd` is the single source of truth for whether the
  // add-page entry points should route to the paywall. `pages.length` feeds
  // the fallback so the gate keeps working (old client-side behavior) when
  // the usage endpoint can't answer.
  const { heatmaps: heatmapsUsage, metadata: planUsageMetadata } = usePlanUsage({
    propertyId,
    fallbackCounts: { heatmaps: pages.length },
  })
  const heatmapLimit = heatmapsUsage.limit
  const heatmapsUsed = heatmapsUsage.current
  const isUnlimitedHeatmaps = heatmapsUsage.isUnlimited
  const atHeatmapLimit = !heatmapsUsage.canAdd
  const displayPlanName = getPlanDisplayName(currentPlan, planUsageMetadata?.planLabel || planName)
  const heatmapLimitCopy = buildPlanLimitCopy({
    noun: 'heatmaps',
    limit: heatmapLimit,
    planName: displayPlanName,
    resolution: 'Remove a page from the page menu, or upgrade for unlimited heatmaps.',
  })

  // Guard the add-page entry points: at the limit, open the existing paywall
  // (reused across the app) rather than the add dialog.
  const handleAddPageClick = () => {
    if (atHeatmapLimit) {
      openPricingModal({ reason: 'heatmap_limit' })
      return
    }
    setShowAddPageDialog(true)
  }

  // Re-arm the dismissible limit banner once the user is back under the limit,
  // so it shows again if they later fill every slot.
  useEffect(() => {
    if (!atHeatmapLimit && bannerDismissed) setBannerDismissed(false)
  }, [atHeatmapLimit, bannerDismissed])

  useEffect(() => {
    return () => {
      if (screenshotPollRef.current) clearInterval(screenshotPollRef.current)
    }
  }, [])

  // Build versions for each page
  useEffect(() => {
    if (pages.length > 0) {
      const versionsMap = new Map<string, ScreenshotVersion[]>()
      const selectedMap = new Map<string, number>()

      pages.forEach((page: any) => {
        const versions = buildVersionsList(page)
        versionsMap.set(page.page_url, versions)
        if (!selectedMap.has(page.page_url)) {
          selectedMap.set(page.page_url, 0)
        }
      })

      setPageVersions(versionsMap)
      setSelectedVersionIndex(selectedMap)
    }
  }, [pages])

  // Auto-select first page when pages load
  useEffect(() => {
    if (pages.length > 0 && (selectedPageIndex < 0 || selectedPageIndex >= pages.length)) {
      setSelectedPageIndex(0)
    }
  }, [pages.length, selectedPageIndex])

  // Check access
  useEffect(() => {
    if (isShareMode) {
      setHasAccess(true)
      setIsCheckingAccess(false)
      return
    }
    const checkAccess = async () => {
      if (!token || !propertyId) {
        setHasAccess(false)
        setIsCheckingAccess(false)
        return
      }
      try {
        setIsCheckingAccess(true)
        const propertiesData = await getAllProperties(token)
        const currentProperty = propertiesData?.properties?.find(
          (p: any) => p.propertyId === propertyId,
        )
        setHasAccess(!!currentProperty?.features?.heatmaps)
      } catch {
        setHasAccess(false)
      } finally {
        setIsCheckingAccess(false)
      }
    }
    checkAccess()
  }, [token, propertyId, isShareMode])

  const selectedPage = pages[selectedPageIndex]
  const selectedPageUrl = selectedPage?.page_url || ''

  const observabilityScope = useMemo(
    () => ({
      propertyId,
      planTier: currentPlan,
      planName,
      userId: user?.userId,
      email: user?.email,
    }),
    [currentPlan, planName, propertyId, user?.email, user?.userId],
  )

  const reportHeatmapError = useCallback(
    (error: any, action: 'add' | 'remove' | 'capture' | 'download', pageUrl?: string) => {
      if (isForbiddenError(error)) {
        if (action === 'download') return

        reportHeatmap403({
          error,
          action,
          userId: user?.userId,
          email: user?.email,
          propertyId,
          planTier: currentPlan,
          planName,
        })
        return
      }

      if (action === 'download') {
        reportHeatmapDownloadFailure(observabilityScope, error, pageUrl)
        return
      }

      reportHeatmapFailure(observabilityScope, action, error, pageUrl)
    },
    [currentPlan, observabilityScope, planName, propertyId, user?.email, user?.userId],
  )

  // Get heatmap data for selected page
  const { data: heatmapDataRaw, isLoading: heatmapDataLoading } = useHeatmapData(selectedPageUrl, {
    propertyId,
    token: heatmapToken,
  })

  // Check if custom date range is selected (not supported by legacy API)
  const isCustomDateRange = (heatmapDataRaw as any)?.isCustomDateRange === true || period.includes(':')

  const heatmapData = useMemo(() => {
    if (!heatmapDataRaw || isCustomDateRange) return { points: [], scrollDepth: null }
    return normalizeHeatmapResponse(heatmapDataRaw)
  }, [heatmapDataRaw, isCustomDateRange])

  // Device split: the API tags each point with the screen-size bucket it came from.
  const filteredHeatmapPoints = useMemo(() => {
    if (!heatmapData.points) return []
    return heatmapData.points.filter((point: any) => deviceOfPoint(point) === selectedDeviceType)
  }, [heatmapData.points, selectedDeviceType])

  // Open on the device that carries the clicks until the user picks one: a
  // mostly-mobile site opened on Desktop showed an empty map.
  const deviceShares = useMemo(() => deviceClickShares(heatmapData.points ?? []), [heatmapData.points])
  const deviceChosenRef = useRef(false)
  useEffect(() => {
    if (deviceChosenRef.current) return
    const dominant = dominantDevice(deviceShares)
    if (dominant) setSelectedDeviceType(dominant)
  }, [deviceShares])
  const chooseDevice = useCallback((device: HeatmapDevice) => {
    deviceChosenRef.current = true
    setSelectedDeviceType(device)
  }, [])

  // Push heatmap data to AI context so the copilot can analyze it
  const aiContext = useAIPageContextSafe()
  const aiContextRef = useRef(aiContext)
  useEffect(() => { aiContextRef.current = aiContext }, [aiContext])

  // Poll for screenshot after page add — backend captures it ~1s after add
  useEffect(() => {
    if (!awaitingScreenshotFor || !pagesData?.pages) return

    const pages: any[] = pagesData.pages
    const targetPage = pages.find((p: any) => {
      const url = p.page_url || ''
      return url.endsWith(awaitingScreenshotFor) || url === awaitingScreenshotFor
    })

    if (targetPage?.desktop_screenshot_url || targetPage?.mobile_screenshot_url) {
      // Screenshot arrived — stop polling
      if (screenshotPollRef.current) {
        clearInterval(screenshotPollRef.current)
        screenshotPollRef.current = null
      }
      setAwaitingScreenshotFor(null)
      toast.success('Screenshot captured!')
    }
  }, [pagesData, awaitingScreenshotFor])

  useEffect(() => {
    const ctx = aiContextRef.current
    if (!ctx) return

    if (selectedPage) {
      const totalClicks = filteredHeatmapPoints.reduce(
        (sum: number, p: any) => sum + (p.count || p.value || 0), 0
      )
      ctx.setCustomData('Heatmap Page', [
        `Page: ${selectedPage.title || selectedPage.page_url}`,
        `URL: ${selectedPage.page_url}`,
        `Device: ${selectedDeviceType}`,
        `Total Clicks: ${totalClicks}`,
        `Unique Click Points: ${filteredHeatmapPoints.length}`,
      ])
    }

    if (heatmapData.scrollDepth && heatmapData.scrollDepth.totalSessions > 0) {
      const sd = heatmapData.scrollDepth
      ctx.setCustomData('Scroll Depth', [
        `Average Scroll: ${sd.averageScroll}%`,
        `Sessions Analyzed: ${sd.totalSessions}`,
        ...(sd.buckets || []).map((b: any) =>
          `${b.startPercent}%-${b.endPercent}%: Reach ${b.reachPercentage}%, Drop-off ${b.dropOffPercentage}%`
        ),
      ])
    }

    if (pages.length > 0) {
      ctx.setCustomData('Tracked Pages', pages.map((p: any) =>
        `${p.title || p.page_url} (${p.page_url})`
      ))
    }
  }, [selectedPage?.page_url, selectedDeviceType, filteredHeatmapPoints.length, heatmapData.scrollDepth, pages.length])

  // Get selected version
  const selectedVersion = useMemo(() => {
    if (!selectedPage) return null
    const versions = pageVersions.get(selectedPage.page_url)
    if (!versions || versions.length === 0) return null
    const versionIdx = selectedVersionIndex.get(selectedPage.page_url) ?? 0
    return versions[versionIdx] || versions[0]
  }, [selectedPage, pageVersions, selectedVersionIndex])

  const screenshotUrl = useMemo(() => {
    if (!selectedVersion) return null
    return selectedDeviceType === 'mobile'
      ? selectedVersion.mobile_screenshot_url
      : selectedVersion.desktop_screenshot_url
  }, [selectedVersion, selectedDeviceType])

  const screenshotTime = useMemo(() => {
    if (!selectedVersion) return null
    return selectedDeviceType === 'mobile'
      ? selectedVersion.mobile_screenshot_at
      : selectedVersion.desktop_screenshot_at
  }, [selectedVersion, selectedDeviceType])

  const versionDateRange = useMemo(() => {
    if (!selectedPage) return null
    const versions = pageVersions.get(selectedPage.page_url)
    if (!versions) return null
    const versionIdx = selectedVersionIndex.get(selectedPage.page_url) ?? 0
    return getDateRangeForVersion(versions, versionIdx)
  }, [selectedPage, pageVersions, selectedVersionIndex])

  const handleAddPage = async (pageUrl: string, title: string | null) => {
    try {
      await addPageMutation.mutateAsync({ pageUrl, title })
      logHeatmapPageAdded(observabilityScope, pageUrl)
      invalidatePlanUsage()
      toast.success('Page added. Capturing screenshot…')
      setShowAddPageDialog(false)

      // Poll every 3s for up to 60s until the backend screenshot is ready
      setAwaitingScreenshotFor(pageUrl)
      if (screenshotPollRef.current) clearInterval(screenshotPollRef.current)
      const deadline = Date.now() + 60_000
      screenshotPollRef.current = setInterval(() => {
        if (Date.now() > deadline) {
          clearInterval(screenshotPollRef.current!)
          screenshotPollRef.current = null
          setAwaitingScreenshotFor(null)
          return
        }
        queryClient.invalidateQueries({ queryKey: ['heatmap-pages', propertyId] })
      }, 3000)
    } catch (error: any) {
      reportHeatmapError(error, 'add', pageUrl)

      if (getHeatmapErrorCode(error) === 'heatmap_page_limit_reached') {
        // Replace the generic 4xx toast with the app's paywall/upgrade prompt.
        setShowAddPageDialog(false)
        openPricingModal({ reason: 'heatmap_limit' })
      } else {
        toast.error(error?.message || 'Failed to add page')
      }
    }
  }

  const handleRemovePage = async (pageUrl: string) => {
    if (
      !confirm('Are you sure you want to remove this page from heatmap tracking?')
    ) {
      return
    }

    try {
      await removePageMutation.mutateAsync(pageUrl)
      invalidatePlanUsage()
      logHeatmapPageRemoved(observabilityScope, pageUrl)
      toast.success('Page removed successfully')
      if (selectedPageIndex >= pages.length - 1) {
        setSelectedPageIndex(Math.max(0, pages.length - 2))
      }
    } catch (error: any) {
      reportHeatmapError(error, 'remove', pageUrl)
      toast.error(error?.message || 'Failed to remove page')
    }
  }

  const handleCaptureScreenshot = async (
    pageUrl: string,
    deviceType: HeatmapDevice | null = null,
  ) => {
    const key = `${pageUrl}-${deviceType || 'both'}`
    setCapturingScreenshots((prev) => new Set(prev).add(key))

    try {
      await captureScreenshotMutation.mutateAsync({ pageUrl, deviceType })
      logHeatmapScreenshotCaptured(observabilityScope, pageUrl)
      toast.success('Screenshot captured successfully')
    } catch (error: any) {
      reportHeatmapError(error, 'capture', pageUrl)
      toast.error(
        isHeatmapScreenshotTimeout(error)
          ? 'Screenshot capture timed out. Try again. The page may be slow to load.'
          : error?.message || 'Failed to capture screenshot',
      )
    } finally {
      setCapturingScreenshots((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  const handleVersionChange = (pageUrl: string, versionIndex: number) => {
    setSelectedVersionIndex((prev) => {
      const next = new Map(prev)
      next.set(pageUrl, versionIndex)
      return next
    })
  }

  const loadImageFromBlob = async (blob: Blob): Promise<HTMLImageElement> => {
    return await new Promise((resolve, reject) => {
      const img = new Image()
      const objectUrl = URL.createObjectURL(blob)
      img.onload = () => {
        URL.revokeObjectURL(objectUrl)
        resolve(img)
      }
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        reject(new HeatmapDownloadAssetError('decode_failed'))
      }
      img.src = objectUrl
    })
  }

  const handleDownloadHeatmap = async () => {
    if (!selectedPage || !screenshotUrl) {
      toast.error('No screenshot available to download')
      return
    }

    setIsDownloadingHeatmap(true)

    try {
      const imageElement = captureRef.current?.getImageElement()
      if (!imageElement || !imageElement.complete) {
        throw new Error('Screenshot image not loaded')
      }

      // Wait for fonts to load
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready
      }
      await new Promise((resolve) => setTimeout(resolve, 200))

      // Prefer the screenshot origin. The historical /api/heatmap-screenshot
      // proxy is not implemented — production serves the SPA HTML with 200.
      const screenshotBlob = await fetchHeatmapScreenshotBlob(screenshotUrl)
      const proxiedImage = await loadImageFromBlob(screenshotBlob)

      const haloCanvas = captureRef.current?.getHaloCanvas()
      const mainCanvas = captureRef.current?.getMainCanvas()

      const baseWidth =
        proxiedImage.naturalWidth ||
        proxiedImage.width ||
        imageElement.naturalWidth ||
        imageElement.width ||
        imageElement.offsetWidth ||
        0
      const baseHeight =
        proxiedImage.naturalHeight ||
        proxiedImage.height ||
        imageElement.naturalHeight ||
        imageElement.height ||
        imageElement.offsetHeight ||
        0

      if (!baseWidth || !baseHeight) {
        throw new Error('Unable to determine screenshot dimensions')
      }

      // Scroll metadata
      const scrollData = heatmapData.scrollDepth
      const bucketList = scrollData?.buckets ?? []
      const bucketCount = bucketList.length
      const padding = 24
      const rowHeight = 26
      const baseSummaryHeight = scrollData && scrollData.totalSessions > 0 ? 140 : 110
      const idealMetadataHeight =
        padding * 2 + baseSummaryHeight + bucketCount * rowHeight ||
        padding * 2 + baseSummaryHeight
      const maxMetadataHeight = Math.max(200, baseHeight * 0.65)
      const metadataHeight = Math.min(
        maxMetadataHeight,
        Math.max(idealMetadataHeight, padding * 2 + baseSummaryHeight),
      )
      const finalWidth = baseWidth
      const finalHeight = baseHeight + metadataHeight
      const metadataY = baseHeight

      const compositeCanvas = document.createElement('canvas')
      compositeCanvas.width = finalWidth
      compositeCanvas.height = finalHeight

      const ctx = compositeCanvas.getContext('2d')
      if (!ctx) {
        throw new Error('Could not create canvas context')
      }

      // Draw screenshot image
      ctx.drawImage(proxiedImage, 0, 0, baseWidth, baseHeight)

      // Draw halo canvas overlay
      if (haloCanvas) {
        ctx.save()
        ctx.globalAlpha = 0.5
        ctx.globalCompositeOperation = 'multiply'
        ctx.drawImage(haloCanvas, 0, 0, baseWidth, baseHeight)
        ctx.restore()
      }

      // Draw main heatmap canvas overlay
      if (mainCanvas) {
        ctx.save()
        ctx.globalAlpha = 0.9
        ctx.drawImage(mainCanvas, 0, 0, baseWidth, baseHeight)
        ctx.restore()
      }

      // Background panel
      const gradient = ctx.createLinearGradient(0, metadataY, 0, finalHeight)
      gradient.addColorStop(0, 'rgba(3, 7, 18, 0.98)')
      gradient.addColorStop(1, 'rgba(10, 10, 15, 0.92)')
      ctx.fillStyle = gradient
      ctx.fillRect(0, metadataY, finalWidth, metadataHeight)
      ctx.strokeStyle = 'rgba(255,255,255,0.08)'
      ctx.strokeRect(0, metadataY, finalWidth, metadataHeight)

      ctx.fillStyle = '#ffffff'
      ctx.textAlign = 'left'
      ctx.textBaseline = 'top'

      const pageTitle = selectedPage.title || selectedPage.page_url
      const deviceLabel = selectedDeviceType === 'mobile' ? 'Mobile' : 'Desktop'
      const dateRange = versionDateRange
        ? formatDateRange(
            versionDateRange.startDate,
            versionDateRange.endDate,
            (selectedVersionIndex.get(selectedPage.page_url) ?? 0) === 0,
          )
        : dateFilter || 'N/A'

      let summaryY = metadataY + padding
      ctx.font = '600 18px "Inter Variable", Inter, system-ui, -apple-system, sans-serif'
      ctx.fillText(pageTitle, padding, summaryY)
      summaryY += 28

      ctx.font = '13px "Inter Variable", Inter, system-ui, -apple-system, sans-serif'
      ctx.fillStyle = '#c8cede'
      ctx.fillText(`${deviceLabel} • ${dateRange}`, padding, summaryY)
      summaryY += 20

      ctx.fillText(`Page URL: ${selectedPage.page_url}`, padding, summaryY)
      summaryY += 18

      ctx.fillText(`Generated: ${new Date().toLocaleString()}`, padding, summaryY)
      summaryY += 24

      // Heatmap stats (total clicks)
      if (heatmapData.points) {
        const totalClicks = filteredHeatmapPoints.reduce(
          (sum, p) => sum + (p.count || p.value || 0),
          0,
        )
        ctx.fillText(`Total Clicks: ${totalClicks}`, padding, summaryY)
        summaryY += 20
      }

      if (scrollData && scrollData.totalSessions > 0) {
        ctx.fillText(
          `Average Scroll: ${scrollData.averageScroll}%`,
          padding,
          summaryY,
        )
        summaryY += 18
        ctx.fillText(`Sessions: ${scrollData.totalSessions}`, padding, summaryY)
        summaryY += 24
      } else {
        ctx.fillText('Scroll data not available for this range.', padding, summaryY)
        summaryY += 24
      }

      const bucketStartY = summaryY + 12
      const bucketAreaHeight = Math.max(
        0,
        metadataY + metadataHeight - padding - bucketStartY,
      )
      if (bucketCount > 0 && bucketAreaHeight > 0) {
        const columns = bucketCount > 12 ? 3 : bucketCount > 6 ? 2 : 1
        const rows = Math.ceil(bucketCount / columns)
        const effectiveRowHeight = Math.max(18, bucketAreaHeight / rows)
        const columnWidth = (finalWidth - padding * 2) / columns

        ctx.textAlign = 'left'
        bucketList.forEach((bucket, index) => {
          const columnIndex = index % columns
          const rowIndex = Math.floor(index / columns)
          const entryX = padding + columnIndex * columnWidth
          const entryY = bucketStartY + rowIndex * effectiveRowHeight

          ctx.font = '600 12px "Inter Variable", Inter, system-ui, -apple-system, sans-serif'
          ctx.fillStyle = '#ffffff'
          ctx.fillText(
            `${bucket.startPercent}% – ${bucket.endPercent}%`,
            entryX,
            entryY,
          )

          ctx.font = '11px "Inter Variable", Inter, system-ui, -apple-system, sans-serif'
          ctx.fillStyle = '#c8cede'
          ctx.fillText(
            `Reach: ${bucket.reachPercentage}% • Sessions: ${bucket.sessionsReached}`,
            entryX,
            entryY + effectiveRowHeight * 0.45,
          )
          ctx.fillText(
            `Drop-off: ${bucket.dropOffPercentage}%`,
            entryX,
            entryY + effectiveRowHeight * 0.75,
          )
        })
      }

      // Convert to blob and download
      const imageBlobOutput = await new Promise<Blob>((resolve, reject) => {
        compositeCanvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create image blob'))
              return
            }
            resolve(blob)
          },
          'image/png',
          1.0,
        )
      })

      const sanitizedPageName = (selectedPage.title || selectedPage.page_url)
        .replace(/[^a-z0-9]/gi, '-')
        .toLowerCase()
      const timestamp = new Date().toISOString().split('T')[0]

      // Download image
      const url = URL.createObjectURL(imageBlobOutput)
      const link = document.createElement('a')
      link.download = `heatmap-${sanitizedPageName}-${selectedDeviceType}-${timestamp}.png`
      link.href = url
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 100)

      toast.success('Heatmap image downloaded!')
    } catch (error: any) {
      reportHeatmapError(error, 'download', selectedPageUrl)
      toast.error(
        isHeatmapDownloadAssetError(error)
          ? heatmapDownloadAssetToastMessage(error.reason)
          : 'Failed to download heatmap: ' + (error.message || 'Unknown error'),
      )
    } finally {
      setIsDownloadingHeatmap(false)
    }
  }

  const handleDownloadScrollDetails = async () => {
    if (!selectedPage) {
      toast.error('No page selected')
      return
    }

    setIsDownloadingScrollDetails(true)

    try {
      const deviceLabel = selectedDeviceType === 'mobile' ? 'Mobile' : 'Desktop'
      const dateRange = versionDateRange
        ? formatDateRange(
            versionDateRange.startDate,
            versionDateRange.endDate,
            (selectedVersionIndex.get(selectedPage.page_url) ?? 0) === 0,
          )
        : dateFilter || 'N/A'

      const scrollData = heatmapData.scrollDepth
      const bucketList = scrollData?.buckets ?? []
      const totalClicks = filteredHeatmapPoints.reduce(
        (sum, p) => sum + (p.count || p.value || 0),
        0,
      )

      const lines: string[] = []
      lines.push(`# Scroll Details – ${selectedPage.title || selectedPage.page_url}`)
      lines.push('')
      lines.push(`- **Device:** ${deviceLabel}`)
      lines.push(`- **Date Range:** ${dateRange}`)
      lines.push(`- **Generated:** ${new Date().toLocaleString()}`)
      lines.push(`- **Page URL:** ${selectedPage.page_url}`)
      lines.push('')
      lines.push('## Summary')
      lines.push(`- **Total Clicks:** ${totalClicks}`)
      if (scrollData && scrollData.totalSessions > 0) {
        lines.push(`- **Average Scroll Depth:** ${scrollData.averageScroll}%`)
        lines.push(`- **Sessions Analyzed:** ${scrollData.totalSessions}`)
      } else {
        lines.push('- **Scroll Data:** Not available for this range.')
      }
      lines.push('')
      lines.push('## Scroll Buckets')
      if (bucketList.length === 0) {
        lines.push('No scroll buckets available yet.')
      } else {
        bucketList.forEach((bucket, index) => {
          lines.push(
            `${index + 1}. **${bucket.startPercent}% – ${bucket.endPercent}%**`,
          )
          lines.push(`   - Reach: ${bucket.reachPercentage}%`)
          lines.push(`   - Sessions Reached: ${bucket.sessionsReached}`)
          lines.push(`   - Drop-off: ${bucket.dropOffPercentage}%`)
        })
      }

      const content = lines.join('\n')
      const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const sanitizedPageName = (selectedPage.title || selectedPage.page_url)
        .replace(/[^a-z0-9]/gi, '-')
        .toLowerCase()
      const timestamp = new Date().toISOString().split('T')[0]
      const link = document.createElement('a')
      link.download = `scroll-details-${sanitizedPageName}-${selectedDeviceType}-${timestamp}.md`
      link.href = url
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 100)

      toast.success('Scroll details downloaded!')
    } catch (error: any) {
      reportHeatmapError(error, 'download', selectedPageUrl)
      toast.error('Failed to download scroll details')
    } finally {
      setIsDownloadingScrollDetails(false)
    }
  }

  const addPageDialog = !isShareMode && (
    <AddPageDialog
      open={showAddPageDialog}
      onOpenChange={setShowAddPageDialog}
      onAdd={handleAddPage}
      isAdding={addPageMutation.isPending}
      trackedUrls={pages.map((p: any) => p.page_url)}
      used={heatmapsUsed}
      limit={heatmapLimit}
      isUnlimited={isUnlimitedHeatmaps}
      atLimit={atHeatmapLimit}
      planName={displayPlanName}
      onUpgrade={() => openPricingModal({ reason: 'heatmap_limit' })}
      domain={context?.property?.domain}
    />
  )

  if (isCheckingAccess || pagesLoading) {
    return (
      <div className={BLEED}>
        <HeatmapLoading />
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className={BLEED}>
        <div className="flex flex-1 flex-col items-center bg-canvas px-4 pt-24">
          <div className="w-full max-w-[560px] rounded-xl bg-card shadow-[0_0_0_1px_var(--border)]">
            <EmptyState
              icon={<HugeiconsIcon icon={Alert01Icon} size={18} />}
              title="Heatmaps are not on your current plan"
              description="Upgrade to see where visitors click and how far they scroll."
            />
          </div>
        </div>
      </div>
    )
  }

  if (pages.length === 0) {
    return (
      <div className={BLEED}>
        <HeatmapNoPages onAddPage={isShareMode ? undefined : handleAddPageClick} />
        {addPageDialog}
      </div>
    )
  }

  const pageKey = selectedPage?.page_url ?? ''
  const versions = pageVersions.get(pageKey) ?? []
  const captureKey = `${selectedPageUrl}-${selectedDeviceType}`
  const menuActions = isShareMode
    ? {}
    : {
        onAddPage: handleAddPageClick,
        addPageAtLimit: atHeatmapLimit,
        onDownloadHeatmap: screenshotUrl ? handleDownloadHeatmap : undefined,
        onDownloadScrollDetails: handleDownloadScrollDetails,
        onRecapture: () => handleCaptureScreenshot(selectedPageUrl, selectedDeviceType),
        onRemovePage: () => handleRemovePage(selectedPageUrl),
        isDownloading: isDownloadingHeatmap || isDownloadingScrollDetails,
        isCapturing: capturingScreenshots.has(captureKey),
        captures: versions.map((v, i) => {
          const at = new Date(v.captured_at)
          const day = format(at, 'MMM d, yyyy')
          // Two captures on one day read as duplicates without the time.
          const sameDay = versions.some((o, j) => j !== i && format(new Date(o.captured_at), 'MMM d, yyyy') === day)
          return {
            value: String(i),
            label: `${day}${sameDay ? `, ${format(at, 'h:mm a')}` : ''}${i === 0 ? ' · latest' : ''}`,
          }
        }),
        selectedCapture: String(selectedVersionIndex.get(pageKey) ?? 0),
        onSelectCapture: (v: string) => handleVersionChange(pageKey, Number(v)),
      }

  const topBarProps = {
    pages,
    selectedIndex: selectedPageIndex,
    onSelectPage: setSelectedPageIndex,
    mode,
    onModeChange: setMode,
    device: selectedDeviceType,
    onDeviceChange: chooseDevice,
    deviceShares,
    period,
    onPeriodChange: (next: string) => updateState({ period: next }),
  }

  if (isCustomDateRange) {
    return (
      <div className={BLEED}>
        <HeatmapTopBar {...topBarProps} visitors={null} {...menuActions} />
        <div className="flex flex-1 flex-col items-center bg-canvas px-4 pt-24">
          <div className="w-full max-w-[560px] rounded-xl bg-card shadow-[0_0_0_1px_var(--border)]">
            <EmptyState
              icon={<HugeiconsIcon icon={Calendar01Icon} size={18} />}
              title="Heatmaps need a preset date range"
              description="Custom ranges are not supported for heatmaps yet. Pick a range from the date menu."
            />
          </div>
        </div>
        {addPageDialog}
      </div>
    )
  }

  if (heatmapDataLoading) {
    return (
      <div className={BLEED}>
        <HeatmapLoading />
      </div>
    )
  }

  const staleCapture =
    versionDateRange &&
    dateFilter &&
    !isDateRangeWithinFilter(versionDateRange.startDate, versionDateRange.endDate, dateFilter)

  const notice =
    (atHeatmapLimit && !isShareMode && !bannerDismissed) || staleCapture ? (
      <div className="flex flex-col gap-3">
        {atHeatmapLimit && !isShareMode && !bannerDismissed && (
          <PlanLimitBanner
            title={heatmapLimitCopy.title}
            description={heatmapLimitCopy.description}
            noun="heatmaps"
            planName={displayPlanName}
            onDismiss={() => setBannerDismissed(true)}
            isOwner={planUsageMetadata?.isOwner ?? true}
          />
        )}
        {staleCapture && (
          <p className="text-[12px] leading-4 text-warning">
            This capture covers a different period than the date range, so some clicks may sit on an older layout.
          </p>
        )}
      </div>
    ) : undefined

  return (
    <div className={BLEED}>
      <HeatmapPageBody
        ref={captureRef}
        {...topBarProps}
        {...menuActions}
        points={filteredHeatmapPoints}
        scrollDepth={heatmapData.scrollDepth}
        screenshotUrl={screenshotUrl}
        capturedAt={screenshotTime}
        basePath={basePath}
        notice={notice}
        onTestClick={(source) => logHeatmapTestStarted(observabilityScope, selectedPageUrl, source, mode)}
      />
      {addPageDialog}
    </div>
  )
}
